
import React, { useState } from "react";
import { Mail, Lock, User, AlertCircle } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { loginSchema } from "./LoginForm";

export const signupSchema = loginSchema.extend({
  full_name: z.string().min(2, { message: "Full name must be at least 2 characters" }),
});

interface SignupFormProps {
  onSuccess?: () => void;
  onModeSwitch: () => void;
}

export const SignupForm = ({ onSuccess, onModeSwitch }: SignupFormProps) => {
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [confirmationSent, setConfirmationSent] = useState(false);

  const form = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      full_name: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof signupSchema>) => {
    setLoading(true);
    setAuthError(null);
    
    try {
      // Sign up with confirmation email
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            full_name: values.full_name,
          },
          emailRedirectTo: window.location.origin + '/auth',
        },
      });

      if (error) throw error;
      
      console.log("Signup response:", data);
      
      if (data.user?.identities?.length === 0) {
        // User already exists
        toast({
          variant: "destructive",
          title: "Sign up failed",
          description: "An account with this email already exists. Please sign in instead.",
        });
        setAuthError("An account with this email already exists. Please sign in instead.");
        
        if (onModeSwitch) {
          setTimeout(() => onModeSwitch(), 2000);
        }
        return;
      }
      
      // Check if email confirmation is required
      if (data.user && data.session === null) {
        setConfirmationSent(true);
        toast({
          title: "Check your email",
          description: "We've sent you an email with a confirmation link to complete your sign up.",
        });
      } else {
        toast({
          title: "Sign up successful!",
          description: "Your account has been created successfully.",
        });
        
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      setAuthError(error.message || "Please try again with different information");
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error.message || "Please try again with different information",
      });
    } finally {
      setLoading(false);
    }
  };

  if (confirmationSent) {
    return (
      <div className="text-center space-y-4">
        <Mail className="mx-auto h-12 w-12 text-madts-purple" />
        <h3 className="text-lg font-medium">Check your email</h3>
        <p className="text-sm text-gray-500">
          We've sent a confirmation link to your email address. Please check your inbox and click the link to complete your registration.
        </p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={onModeSwitch}
        >
          Back to login
        </Button>
      </div>
    );
  }

  return (
    <>
      {authError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{authError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="full_name">Full Name</FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="full_name"
                      type="text"
                      placeholder="John Doe"
                      className="pl-10"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input placeholder="your@email.com" className="pl-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input type="password" placeholder="••••••" className="pl-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button 
            type="submit" 
            className="w-full bg-madts-purple hover:bg-madts-purple/90"
            disabled={loading}
          >
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </form>
      </Form>

      <div className="mt-6 text-center">
        <p className="text-gray-600">
          Already have an account?
          <Button 
            variant="link" 
            className="text-madts-purple"
            onClick={onModeSwitch}
          >
            Sign in
          </Button>
        </p>
      </div>
    </>
  );
};
